// ─────────────────────────────────────────────────────────────────────
// ENGINE CHANGELOG — version history for the platform engine (PlatformEngine).
// Pure data, extracted verbatim from the monolith (legacy/App.tsx) in PR-AM.
// Surfaced via `Padel24Engine.changelog` on the engine doc page
// (src/components/help/EngineVersioningSection).
// ─────────────────────────────────────────────────────────────────────

export interface EngineChangelogEntry {
  version: string;
  date: string;
  name: string;
  changes: string[];
}

export const ENGINE_CHANGELOG: EngineChangelogEntry[] = [
  {
    version: '1.15.0',
    date: '2026-09-09',
    name: 'KM-sluttspill, banepad-resolver & Reporting',
    changes: [
      'KM: buildDivisionKnockoutMatches bygger nå de senere rundene (SF/F, bronse på forespørsel) også for',
      '  klasser UTEN gruppespill (ren cup) via buildSubsequentARounds — før stoppet cup-grenen etter første runde.',
      'KM: divisjoner som ikke er en toerpotens får byes i første runde i stedet for ingen bracket.',
      "KM: determineBracketStructure(1, n, 'semifinals'|'final') — tvunget sluttspill for én gruppe (topp 4 / topp 2);",
      '  buildCrossOverBracket seeder 1–4 / 2–3 (semi) eller 1–2 (finale) fra gruppetabellen (options.bracketSize).',
      "  NY defaultSmallClassFormat(n) → 'group' (tabellen avgjør, sluttspill er opt-in). rebuildKnockoutForDivision",
      '  bruker gruppespill fra én gruppe og oppover (groupCount >= 1).',
      'Banepad: banepadResolveCourt tar paden KUN når en kamp faktisk pågår (r.current) eller er forced — kamper uten',
      '  starttid (americano/mexicano) resolver ikke lenger som «neste» for alltid. Golden-master oppdatert (krav endret).',
      'Banepad: forceSingleMatch- og forceSeries-parametre; lokale planlagte enkeltkamper (planned_matches_v1, lenket via',
      '  plan.bookingId) normaliseres til pad-form; banepadBookingTag tar et valgfritt plannedMatches-argument.',
      'Banepad: NY banepadForceShouldClear (auto-klarering av manuell force ved ferdig turnering / passert vindu) og',
      '  NY banepadCourtContext (autoForceForCourt + courtCtx: manuell > auto > normal) — auto-force-vinduet.',
      'Banepad: NY banepadDevice (paringskode med TTL, enhets-token, pairWithCode) for pads på egne nettbrett.',
      'Enkeltkamp: planlagte kamper bærer levelMin/levelMax (additivt i addPlannedMatch) og kan ha åpne plasser (null-id).',
      'NY reporting.ts: buildLedger (bookinger + aktivitetspåmeldinger + seriespill-sesongavgifter → kvitteringsrader),',
      '  filterLedger (måned/senter/demo), settle (brutto, prosentavgift per betalt rad + fast månedsavgift, netto),',
      '  ledgerToCsv (semikolon + BOM) og lokale datohjelpere (aldri toISOString). registrationAmount speiler',
      '  ActivitiesView-prisingen (kvitteringsbeløp > KM-klassepris > pris per spiller ×2 for par).',
      'NY lib/money.ts: formatNok (nb-NO, tusenskille, desimaler kun ved behov) + roundOre.',
    ],
  },
  {
    version: '1.14.0',
    date: '2026-06-04',
    name: 'Booking-motor II',
    changes: [
      'NY booking.courtFacilities(courtNum, courtMeta, courtDefaults): effektive fasiliteter per bane',
      '  (single/dobbel, runoff, streaming) med per-bane-overstyring over globale courtDefaults.',
      'calcBookingPrice: standardpris (cfg.defaultPricePerHour) for minutter uten sone, og per-sone',
      "  priceMode ('replace' = sonens pris erstatter standard, 'add' = legges på toppen). Verifisert i Node.",
      'zoneMatches: nytt excludeHolidays-flagg (sone gjelder aldri på helligdag). includeHolidays/onlyHolidays beholdt.',
      "createBooking bevarer nå title, seriesId, recur og source ('user'|'admin'|'tournament').",
      'findFreeSlots/validateBooking/matrix: over-midnatt åpningstid (close <= open → +1440).',
      'expandRecurrence + recur lagres på serie-bookinger så mønsteret kan redigeres senere.',
      'MERK: random-finaler i Padel24 trekkes IKKE lenger automatisk i samme klikk som siste fase fylles',
      '  (to-klikks-flyt). fillRandomResults fyller kun kamper; finale-trekking skjer ved neste klikk.',
    ],
  },
  {
    version: '1.13.0',
    date: '2026-05-28',
    name: 'Klubbmesterskap II',
    changes: [
      'KM ELO «big win» er nå GAME-BASERT (kmMatchMov) i stedet for sett-basert.',
      '  Super-tiebreak-sett teller 1-0, så jevn 3-setter avgjort 10-0 i STB blir IKKE big win.',
      '  recalcKMElo bruker kmMatchMov(score) (var calcMovMultiplier på settvinninger). Verifisert i Node.',
      'NY: applyCourtPriority(tournament, excludeIds) — gjenbrukbar bane-prioritering trukket ut',
      '  av scheduleKMMatches og eksponert i engine. Kalles ved oppretting og fra kalenderens omstokking.',
      'NY config.forceCourtPriority: lar en dårligere bane stå tom for å gi topp-kampen den beste',
      '  LEDIGE banen i tidsluken (ikke bare blant banene rotasjonen havnet på). Verifisert i Node.',
      'NY config.rankedCourtCount: kun de N øverste banene rangeres, resten fordeles tilfeldig (null = alle).',
      'createKlubbmesterskapTournament: config.name lagres nå (pause/arkiv-lister viste «Turnering»);',
      '  Mix krever ALLTID 1 herre + 1 dame — også i Åpen klasse; nye config-felt force/rankedCourtCount.',
      'BANE-/DIVISJON-BEVISST MATCHING: bracket-gruppekolonne og computeKMClassStandings matcher nå',
      '  par på BÅDE groupId OG divisionId (gruppe-IDer er ikke unike på tvers av divisjoner).',
      'WO i banepad lagrer woAt-tidsstempel; ekskluderes fra ELO som før.',
    ],
  },
  {
    version: '1.12.0',
    date: '2026-05-27',
    name: 'Lucky Looser',
    changes: [
      'NY FUNKSJON: Lucky Looser — valgfritt config-flagg (config.luckyLooser, default true)',
      'Hver gruppes vinner rykker opp og spiller 8.-plass (bronsefinale) i gruppen over',
      'Gruppens plass 8+ skyves én ned; sistemann rykker ned til 1.-plass i gruppen under',
      'Påvirker KUN finaletrekningen — ikke opp/ned mellom faser',
      'IMPLEMENTERT SOM SLOT-MAPPING (Mekanikk B): generateFinalsStructure tar',
      '  luckyLooser-param og endrer hvilken (gruppe, plassering) hver finale-slot',
      '  REFERERER. groupRankings forblir uberørt — D8 er D8 i lista.',
      '  slot 1 → gruppe-over plass N; slot 8 → gruppe-under plass 1;',
      '  slot 9..N → egen gruppe plass 8..(N-1); slot 2-7 → uendret.',
      'Forkastet Mekanikk A (applyLuckyLooser — ranking-omrokering): ga inkonsistent',
      '  visning der placeholder og resolvet spiller ikke stemte. Funksjonen er slettet.',
      'resolvePlaceholders overfører luckyMove/luckyFrom fra slot.luckyHint',
      'fillRandomResults trekker finaler eksplisitt når siste fase er ferdig',
      '  (random-knappen omgår recordResult sin allCompleted-blokk)',
    ],
  },
  {
    version: '1.11.0',
    date: '2026-05-18',
    name: 'Klubbmesterskap',
    changes: [
      'NY TURNERINGSTYPE: Klubbmesterskap — par-basert turnering med gruppespill + sluttspill',
      'Engine eksponert som Padel24Engine.klubbmesterskap med:',
      '  - snakeSeedGroups(pairs, groupCount): serpentine seeding (16/4 gir A=1,8,9,16 osv)',
      '  - roundRobinSchedule(pairs): Berger-tabellen for balansert gruppespill',
      '  - buildCrossOverBracket(groups, advanceTopN): A1-B2/C1-D2/B1-A2/D1-C2-mønster',
      '  - fipBracketSeed(pairs): FIP-stil bracket-seeding (4/8/16/32 par)',
      '  - calcGroupStandings: padel-standard rangering (poeng → settdiff → gamediff → innbyrdes → seed)',
      '  - create({name, classes, courts, days, ...}): hovedfabrikk',
      '  - calcBudget: banetid-sjekk mot kalender',
      'Datamodell: par-basert (faste makkere hele turneringen, modsetning til Americano/Mexicano)',
      'Klasse-system: én turnering kan ha flere kategorier (Herrer/Damer/Mix), hver med ABC-klasser',
      'Match-format: best of 3 m/super-tiebreak (FIP-standard) som default',
      'Verifisert i Node-prototype: 16 par/4 grupper, 24 par/6 grupper, 32 par/8 grupper alle OK',
      'NY storage-key: klubbmesterskap_setups_v1',
    ],
  },
  {
    version: '1.10.0',
    date: '2026-05-18',
    name: 'Familieformater',
    changes: [
      'SEKS NYE TURNERINGSTYPER bygget på Americano/Mexicano-motorene:',
      '  - Mix Mexicano (MMX): mexicanoMode + mixicanoMode, krever kjønnsblanding (1 dame + 1 mann per lag)',
      '  - Team Mexicano (TMX): teamMode + mexicanoMode, faste lag + stillings-basert lag-matching, pool 4/6/8',
      '  - Mix Americano (MAM): kun mixicanoMode, Americano-rotasjon med kjønnsblandings-krav',
      '  - Club Americano (CAM): clubMode, to klubber, hver bane har 1 fra A som makker med 1 fra B',
      '  - Club Team Americano (CTA): clubMode + teamMode, round-robin mellom lag på tvers av klubber',
      '  - Club Mexicano (CMX): clubMode + mexicanoMode, klubb-rang-basert (best vs best), pool 4/6/8',
      'Nye engine-funksjoner:',
      '  - buildMixHistory, generateMixicanoRound, createMixicanoTournament',
      '  - buildTeamHistory, generateTeamMexicanoPool4/6/8Plan, generateTeamMexicanoRound, createTeamMexicanoTournament',
      '  - generateMixAmericanoRound, createMixAmericanoTournament',
      '  - buildClubPairs, generateClubAmericanoRound, calcClubStandings, generateClubAmericanoFinals, createClubAmericanoTournament',
      '  - buildClubTeamHistory, generateClubTeamAmericanoRound, generateClubTeamAmericanoFinals, createClubTeamAmericanoTournament',
      '  - buildClubMexicanoPairs, generateClubMexicanoRound, generateClubMexicanoFinals, createClubMexicanoTournament',
      'Tennis-sett-modus (endMode="first_set"): sett vunnet → kamp fryses, frys-modal vises',
      'Bingo-modus utvidet: frys-modal når spiller fyller bingo-brett',
      'Server-rotasjon for Club-familien: config.initialServer (random), alternerer per runde + finaler',
      'Klubb-rang-basert finaler: A#1+A#2 vs B#1+B#2 (gull), A#3+A#4 vs B#3+B#4 (bronse), osv.',
      'calcClubStandings inkluderer nå finaler i totalsummen (tidligere ekskludert)',
      'handleStart clamper config.courts til effectiveCourts (faktisk antall tilgjengelige)',
      'NYE storage-nøkler: mixicano_setups_v1, team_mexicano_setups_v1, mix_americano_setups_v1, club_americano_setups_v1, club_team_americano_setups_v1, club_mexicano_setups_v1',
      'Verifisert med Node-prototype: Club Mexicano pool 8 gir 47 unike makkerpar (max rep 2) i 16sp/8r-test',
    ],
  },
  {
    version: '1.9.0',
    date: '2026-05-16',
    name: 'Mexicano',
    changes: [
      'NY TURNERINGSTYPE: Mexicano — stillings-basert matchmaking',
      '  - tournamentType: "mexicano" + abbrev "MX" (oransje #f97316)',
      '  - createMexicanoTournament: type-level "mexicano", config arver Americano + 3 nye felter',
      '  - generateMexicanoRound: bygger runder ved å sortere på poeng → dele 4-og-4 på baner',
      'Pool-størrelse for variasjon: 4 (klassisk) / 6 (default) / 8 (max)',
      '  - pickMexicanoFour: velger 4 av N kandidater som gir minst makker-repetisjon',
      '  - mexicanoCombinations + scoreMexicanoCombo: kostfunksjon med 10x vekt på makker, 1x på motstander',
      'Par-konstellasjon: K2 (1+3 vs 2+4) prefereres, så K3 (1+4 vs 2+3), K1 (1+2 vs 3+4) som siste utvei',
      'Tilfeldige runder: config.randomFirstRounds (0-3) + config.randomEveryNthRound (0/3/4/5)',
      '  - opts.forceRandom = true for manuell "Tilfeldig runde nå"-knapp',
      'Validering: pool ≤ halvparten av spillerantall (med fallback til lavere pool)',
      'Gjenbruker calcStandings, generateFinals, buildHistory, fillPlaceholder, replacePlayer fra Americano',
      'Eksponert som Padel24Engine.mexicano med samme API som Padel24Engine.americano',
    ],
  },
  {
    version: '1.8.1',
    date: '2026-05-15',
    name: 'Setups & flyt',
    changes: [
      'Ingen ELO-endringer i denne patch-versjonen',
      'Mindre justering: previewMatches resettes på flere strukturelle config-endringer',
      'applyPreset resetter nå ALLE Padel24-standard-felter (faser, finale, opp/ned, drawing mode, pacing, tider, baner, pauser)',
    ],
  },
  {
    version: '1.8.0',
    date: '2026-05-14',
    name: 'Team Americano',
    changes: [
      'NY TURNERINGSTYPE: Team Americano — faste makkerpar gjennom hele turneringen',
      '  - config.teamMode (boolean) + config.teams ([{p1Id, p2Id}, ...])',
      '  - tournamentType: "team_americano" + abbrev "TAM" (lime-grønn #a3e635)',
      '  - createAmericanoTournament validerer delelig på 4 spillere + komplett par-sett',
      'NY ENGINE-FUNKSJONER:',
      '  - generateTeamAmericanoRound: bevarer par i hver kvartett (2 lag mot 2 lag)',
      '  - buildTeamHistory + calcTeamRoundCost: opponent-historikk og ELO-balanse per lag',
      '  - randomTeamRoundPlan + generateTeamAmericanoRoundPlan: kostfunksjons-søk uten partner-bytte',
      '  - selectBenchedTeams: sittover-balansering per lag (ikke per spiller)',
      '  - generateTeamAmericanoFinals: lag-finaler 1v2 (gull), 3v4 (bronse), osv. — finalsFormat ignoreres',
      'STANDINGS i team-mode:',
      '  - Begge spillere på samme lag har identiske tall + rank',
      '  - Etter sortering omregnes rank som LAG-rang (1, 2, 3, 4 — ikke 1, 1, 3, 3)',
      '  - AmericanoStandingsTab grupperer spillere på samme lag i én rad',
      'SITTOVER-LOGIKK per lag:',
      '  - Hvis en spiller på et lag er skadet → hele laget sitter over',
      '  - benched-listen lagres som spiller-id-er (kompatibel med eksisterende UI)',
      '  - Lag med færrest sittover får prioritet til å spille',
    ],
  },
  {
    version: '1.7.0',
    date: '2026-05-14',
    name: 'Justerbar motor',
    changes: [
      'ELO_K_FACTOR senket fra 64 til 32 — etablerte spillere får mer stabil ELO',
      'NY-SPILLER-BOOST: lineær avtak med grace-periode',
      '  - NEWBIE_GRACE_MATCHES (5): full 2x boost de første N kampene',
      '  - NEWBIE_MATCH_THRESHOLD (20): kamper til ny spiller er etablert',
      '  - NEWBIE_K_MULTIPLIER (2.0): maksimal boost-faktor',
      '  - Kamp grace+1 til threshold: lineær avtak mot 1x',
      '  - Effektiv terskel skaleres med kMultiplier (americano 0.5 = 40 kamper)',
      '  - Asymmetrisk: bare laget med ny spiller får boost',
      '  - Math.max(p1mul, p2mul) per lag — én ny spiller smitter til makker',
      'NEWBIE_INACTIVE_DAYS: 180+ dagers inaktivitet utløser tilbakekomst-boost',
      'getPlayerKMultiplier(player, now, kMultiplier) og getPlayerStatus(player, now, kMultiplier)',
      'formatBoost(mul) — kompakt boost-tekst: "2x", "1.5x" (uten desimaler for heltall)',
      'RANKINGORDER-KONFIGURASJON: drag-and-drop prioritering av rangerings-kriterier',
      '  - compareForRanking(a, b, standings, matches, rankingOrder)',
      '  - rankGroups, applyPromotionDemotion, computeFinalRanking tar rankingOrder',
      '  - calcAmericanoStandings bruker config.rankingOrder',
      '  - DEFAULT_PADEL24_RANKING_ORDER: wins → gamesDiff → h2h → gamesWon (alle på)',
      '  - DEFAULT_AMERICANO_ORDER: wins → pointsDiff → points → h2h (alle på)',
      '  - Bakoverkompat: gamle tieBreaker/h2hPosition konverteres automatisk',
      'EngineSettings — mutable settings-objekt med apply/reset/serialize/defaults',
      '  - Persistert til Storage som engine_settings_v1',
      '  - K_FACTOR, NEWBIE_MATCH_THRESHOLD, NEWBIE_GRACE_MATCHES, NEWBIE_K_MULTIPLIER, NEWBIE_INACTIVE_DAYS, MOV_MAX_BONUS',
      'applyEloUpdate tracker matchesPlayed og lastMatchAt automatisk på alle 4 spillere',
      'applySingleMatchElo tracker også matchesPlayed og lastMatchAt',
      'Match-objekter lagrer t1NewbieMul og t2NewbieMul ved kampslutt',
      '  - addSingleMatch/updateSingleMatch lagrer dem i match.result',
      '  - Migrering ved init reverse-engineerer for gamle enkeltkamper',
      '  - Vises som "2x"-badge i kamp-rader (UniversalMatchRow, PlayerCell, PlayerMatchRow, PlayerSingleMatchRow)',
      '  - Lag-pille viser basis-tall (uten boost), spiller-pille viser faktisk (med boost)',
      'PLAYER_DB-spillere får default matchesPlayed: 30 + seed*100',
      '  - Forhindrer at demo-spillere blir flagget som nye spillere',
    ],
  },
  {
    version: '1.6.0',
    date: '2026-05-14',
    name: 'Banepad og innbyrdes',
    changes: [
      'Americano endMode forenklet — race fjernet, kun total/bingo/time igjen',
      '  - UI er nå strukturert som Poeng (=total, 24 default) vs Games (Bingo eller Tid)',
      '  - Race-modus eksisterer ikke lenger som valgbar opsjon',
      'h2hPosition-config i Americano — innbyrdes oppgjør plasserbart i sort-rekkefølge',
      '  - "off" (default), "before" (før poengdiff), "after_diff" (etter poengdiff), "after" (etter poeng)',
      '  - calcAmericanoStandings bygger h2h-tabell fra motstander-kamper og bruker i tiebreak',
      '  - Likestilte spillere får delt rank kun hvis ALT inkludert h2h er likt',
      'Tids-modus utvidet i Americano:',
      '  - cooldownSeconds (default 60) — klargjørings-tid før kampen starter automatisk',
      '  - endSound (alarm/siren/bell/whistle/beeps/off) — valgbar lyd ved kampslutt',
      '  - cooldownEndSound (whistle/bell/beeps/off) — lyd når kampen blåses i gang',
      '  - voiceWarnings — objekt med per-tidspunkt-toggles (5min, 4min, 3min, 2min, 1min, 30sek, timeUp)',
      '  - alarm-loop ved kampslutt, stoppes via dedikert knapp',
      'Padel24 padFlowMode-config — banepad-rotasjon (result/time)',
      '  - "result" (default for Padel24) — banepad bytter umiddelbart ved resultat-innleggelse',
      '  - "time" — banepad bytter når neste kamps starttid faktisk kommer',
      '  - Pauser kan vises stort også når flow=result, før de egentlig har startet',
    ],
  },
  {
    version: '1.5.0',
    date: '2026-05-13',
    name: 'Konfigurerbar engine',
    changes: [
      'ELO kMultiplier — skalér ELO-effekt per turnering (calcEloChange + applyEloUpdate)',
      '  - Default 1.0 (Padel24, enkeltkamper). Americano bruker 0.5 (50%) siden kampene er kortere',
      '  - Signatur: calcEloChange(teamElo, oppElo, result, mov, kMultiplier=1)',
      'Americano endMode utvidet fra 2 til 4 modi: total, race, bingo, time',
      '  - bingo: første lag som når mål stopper alle pågående kamper i runden',
      '  - time: hele runden spilles på tid (timeMinutes-config)',
      '  - scoreMode bevart for bakoverkompat (race→race, ellers→total)',
      'benchedReward-config for spillere som sitter over en runde',
      '  - "tie" (DEFAULT): uavgjort + snittpoeng (rettferdig på begge tieBreakers)',
      '  - "avg": kun snittpoeng',
      '  - "win": full seier + 60% snittpoeng (kompenserende)',
      '  - "skip": ingenting registreres',
      'calcAmericanoStandings: delt rank ved likestilling (klassisk competition ranking 1,1,3)',
      '  - Tidligere ga likestilte spillere unike rank — nå deler de samme plass',
      'Skade-håndtering for Americano (player.injured = true)',
      '  - generateRound filtrerer ut skadede spillere fra nye runder',
      '  - calcStandings ignorerer skadede i rank-beregning (rank=null)',
      '  - Skadede sorteres alltid nederst, beholder eksisterende ELO og poeng',
      '  - Reversibelt: injured=false bringer spilleren tilbake fra neste runde',
      'yearStartElo: bruker playerEloPre fra første in-year-kamp',
      '  - Tidligere: yearStartElo === current_elo for spillere uten i-fjor-turneringer',
      '  - Nå: rekonstruerer ELO-en FØR spillerens første kamp i året',
    ],
  },
  {
    version: '1.4.0',
    date: '2026-05-13',
    name: 'Pause og bytte',
    changes: [
      'Unike ID-er på alle turneringer (createDemo, createAmericano, wizard)',
      '  - Format: demo_*, am_*, pdl_* — alle har timestamp + random suffix',
      '  - Forhindrer ID-kollisjon når flere turneringer pauses samtidig',
      'replacePlayerInAmericano: skade-bytte for Americano (ny funksjon)',
      '  - Bytter spiller i kommende kamper, lagrer replacement-historikk',
      '  - Eksponert som Padel24Engine.americano.replacePlayer',
      'originalCompletedCount lagres på slot ved replacement (Padel24)',
      '  - Skjuler innbytter-badge når original ikke hadde rukket å spille',
      '  - Brukes av ReplacementBadge i UI til å skjule badge ved 0',
      'fillPlaceholderInAmericano: fyll inn reservert plass uten replacement-historikk',
      'pacingMode default endret fra "even" til "one_gap"',
      '  - Mindre dødtid mellom kamper i samme fase (~40 min vs ~75)',
      'Replacement-historikk lagres både på match og tournament-nivå (Americano)',
    ],
  },
  {
    version: '1.3.0',
    date: '2026-05-12',
    name: 'Enkeltkamper',
    changes: [
      'Enkeltkamper-modul: registrer kamper utenom turneringer (Best av 3, padel-regler)',
      'Weighted Elo MOV-formel for enkeltkamper (Angelini et al., 2022)',
      '  - Game-proporsjon avgjør dominans: 6-0 6-0 = MOV 2.0, 7-6 7-6 = MOV 1.08',
      '  - 2-0 i sett betyr ikke automatisk "big win" — games er det som teller',
      'Tournament MOV-formel uendret (1.0-1.5 basert på score-margin)',
      'Pre-match ELO lagres per spiller på hver kamp (playerEloPre)',
      '  - Brukes for daværende ELO i kamphistorikk',
      '  - Brukes for snitt-ELO før kampen i Lag-ELO-celle',
      'Per-match ELO i tournament recordResult og fillRandom',
      'TOURNAMENT_TYPES: padel24, americano, mexicano,',
      '  klubbmesterskap, single_matches — hver med farge og forkortelse',
      'getEffectivePlayerDB: allPlayers er nå autoritativ ELO-kilde',
      '  - Sync av allPlayers ved arkivering av turnering',
      '  - Konsistent ELO mellom enkeltkamper, turneringer og spillerprofil',
    ],
  },
  {
    version: '1.2.0',
    date: '2026-05-11',
    name: 'Nivåskala',
    changes: [
      '9-nivå ELO-skala (500-3000) erstatter 4-nivå systemet',
      'Hvert nivå har detaljerte skill-beskrivelser for selvplassering',
      'Nivå 1 (Nybegynner) til Nivå 9 (Toppnivå i klubben)',
      '~300 ELO mellom nivåer for tydelig progresjon',
      'Inspirert av Playtomic, MATCHi og NTRP-tilpasninger',
    ],
  },
  {
    version: '1.1.0',
    date: '2026-05-10',
    name: 'Pacing',
    changes: [
      'Tre pacing-modi for tidsfordeling av kamper i samme fase',
      '  - even (default): jevn rytme, 105 min snitt ventetid',
      '  - back_to_back: R1 og R2 rett etter hverandre, 4t 45m totalt',
      '  - one_gap: én kamp i mellom, 5t 33m totalt',
      'ELO_LEVELS sentralisert (Nybegynner/Mosjonist/Konkurranse/Elite)',
      'yearStartElo — årlig baseline-ELO for ranking-reset',
    ],
  },
  {
    version: '1.0.0',
    date: '2026-05-10',
    name: 'Speilrotasjon',
    changes: [
      'Initial engine-API med versjonering',
      'ELO med K=64 + Margin-of-Victory bonus (opp til +50%)',
      'Speilrotasjon-modus: R1-makkere blir R2-motstandere',
      'Variert-modus med 4 prioriterte regler',
      'Rangerte finaler basert på finalNumber (1.-N. plass)',
      'Persistent ELO på tvers av turneringer',
    ],
  },
];
